import {
  createUnifiedTheme,
  defaultTypography,
  createBaseThemeOptions,
  palettes,
  genPageTheme,
} from '@backstage/theme';

// Univerus colour foundations supplied 7 October 2026.
// Teal-500 is decorative; teal-600/700 provide contrast for light-mode controls.
const universusPrimary = '#34a7ad'; // Primary brand color - teal/turquoise
const universusSecondaryDark = '#0c2f50'; // Secondary dark blue
const universusPrimaryText = '#278085'; // Teal-600: light-mode controls, links and focus
const universusPrimaryLight = '#5ec4c9'; // Teal-400: dark-mode links
const universusGray = '#e6e7e8'; // Light gray: dark-mode body text, sidebar text
const universusPrimaryDark = '#20686c'; // Teal-700: hover states and dark-mode accents
const universusSky = '#c2e0fa'; // Pale blue: dark-mode secondary
const universusLight = '#ffffff'; // White - for dark mode text
const universusSidebarBg = '#0c2f50'; // Prototype navigation navy

/**
 * Brand colours for surfaces that stay the same in both colour modes, such as
 * the homepage hero. Everything mode-aware should use the theme palette.
 */
export const brand = {
  navy: universusSecondaryDark,
  navyDeep: '#072238',
  teal: universusPrimary,
  mist: '#dff1f2',
} as const;

// Page headers are flat in every section: the page background with heading
// text (navy in light mode, light grey in dark mode). Backstage's header,
// header labels, favourite star and entity menu all take their colours from
// the page theme, so this one setting gives every page the same header.
const lightPageBackground = 'hsl(220, 18%, 97%)';
const pageThemeKeys = [
  'home',
  'catalog',
  'documentation',
  'apis',
  'teams',
  'tool',
  'service',
  'website',
  'library',
  'other',
  'app',
] as const;
const flatPageThemes = (background: string, fontColor: string) => {
  // genPageTheme builds backgroundImage as "<shape>, <gradient>"; 'none' is
  // a valid empty first layer, so only the flat gradient paints.
  const flat = genPageTheme({
    colors: [background],
    shape: 'none',
    options: { fontColor },
  });
  return Object.fromEntries(pageThemeKeys.map(key => [key, flat]));
};

// Selected prototype theme settings; keep existing Backstage component APIs.
const fontFamily =
  '"Segoe UI", "Segoe UI Web (West European)", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif';
const typography = {
  ...defaultTypography,
  fontFamily,
  body1: { fontSize: 14, lineHeight: '20px' },
  body2: { fontSize: 12, lineHeight: '18px' },
  button: {
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none' as const,
    letterSpacing: 0,
  },
};
const surfaceComponents = (dark: boolean) => {
  const border = dark ? 'rgba(255,255,255,0.16)' : 'hsl(220, 14%, 88%)';
  // Relationship diagrams (catalog graph, Inspect entity > Ancestry and the
  // plain DependencyGraph). Backstage draws related entities in primary.light
  // with primary.contrastText; this theme's pale primary.light made that
  // white-on-mint in light mode, and in dark mode the focused entity
  // (secondary) matched its neighbours. Explicit pairs keep both readable and
  // distinct in each mode.
  const graphRelated = dark
    ? { fill: universusPrimaryDark, text: universusLight }
    : { fill: brand.mist, text: universusSecondaryDark };
  const graphFocused = dark
    ? { fill: brand.mist, text: universusSecondaryDark }
    : { fill: universusSecondaryDark, text: universusLight };
  return {
    // Flat header shape and title, matching the page theme above.
    BackstageHeader: {
      styleOverrides: {
        header: { boxShadow: 'none', borderBottom: `1px solid ${border}` },
        // 26px, the same as the Products plugin's text.page, so Hub pages and
        // Products pages share one title size.
        title: {
          fontSize: 26,
          fontWeight: 600,
          lineHeight: 1.25,
          letterSpacing: '-.5px',
        },
      },
    },
    BackstageContentHeader: {
      styleOverrides: { container: { marginBottom: 8 } },
    },
    // Every graph node is wrapped in this named group, so one rule reaches the
    // catalog graph and the Ancestry tab, whose own node styles are unnamed.
    // The element in each selector outranks the renderers' ".x.primary" rules.
    BackstageDependencyGraphNode: {
      styleOverrides: {
        node: {
          '& rect.primary': {
            fill: graphRelated.fill,
            stroke: graphRelated.fill,
          },
          '& rect.secondary': {
            fill: graphFocused.fill,
            stroke: graphFocused.fill,
          },
          // Labels and kind icons
          '& .primary:not(rect)': { fill: graphRelated.text },
          '& .secondary:not(rect)': { fill: graphFocused.text },
        },
      },
    },
    // Plain DependencyGraph nodes carry no primary/secondary class.
    BackstageDependencyGraphDefaultNode: {
      styleOverrides: {
        node: { fill: graphRelated.fill, stroke: graphRelated.fill },
        text: { fill: graphRelated.text },
      },
    },
    // Named Backstage overrides survive production class-name generation.
    BackstageTable: {
      styleOverrides: {
        root: {
          minWidth: 0,
          '& > :last-child': {
            boxShadow: 'none',
            border: `1px solid ${border}`,
            borderRadius: 8,
          },
          '& tbody td': {
            fontSize: 14,
            lineHeight: 1.5,
            wordBreak: 'normal',
            overflowWrap: 'break-word',
            borderBottom: `1px solid ${border}`,
          },
          '& tbody td a': { fontWeight: 500 },
          // Row action icon buttons (view, edit, star): smaller glyphs with more
          // air between them. Matches icon buttons only, not text buttons.
          '& tbody td .MuiIconButton-root': { padding: 6 },
          '& tbody td .MuiIconButton-root svg': {
            fontSize: 18,
          },
          '& tbody tr:hover td': {
            backgroundColor: dark ? '#173e48' : '#f0f7f7',
          },
          '& a:focus-visible, & button:focus-visible': {
            outline: `2px solid ${
              dark ? universusPrimary : universusPrimaryText
            }`,
            outlineOffset: 2,
          },
        },
      },
    },
    BackstageTableHeader: {
      styleOverrides: {
        header: {
          backgroundColor: dark ? '#192b38' : '#f0f2f5',
          color: dark ? universusGray : '#515d70',
          borderTop: `1px solid ${border}`,
          borderBottom: `1px solid ${border}`,
          fontFamily,
          fontSize: 12,
          fontWeight: 600,
          textTransform: 'none' as const,
          letterSpacing: 0,
        },
      },
    },
    BackstageTableToolbar: {
      styleOverrides: {
        root: {
          padding: '16px 0 16px 20px',
          gap: 12,
          flexWrap: 'wrap' as const,
        },
        title: { '& h2': { fontSize: 20, fontWeight: 600, lineHeight: 1.4 } },
        searchField: { paddingRight: 16 },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: {
          minWidth: 'min(320px, calc(100vw - 32px))',
          maxWidth: 'calc(100vw - 32px)',
        },
        paper: {
          border: `1px solid ${border}`,
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(7,34,56,.12)',
          marginTop: 4,
        },
        listbox: {
          padding: 4,
          maxHeight: 320,
          // Lets long owner names wrap in the catalog Owner filter. The picker
          // exposes no named class for its labels, only this test id
          // (plugin-catalog-react 1.21.2, EntityOwnerPicker ListboxProps).
          '&[data-testid="owner-picker-listbox"] p': {
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
            overflowWrap: 'anywhere',
            fontSize: 14,
            lineHeight: 1.4,
          },
        },
        option: {
          // Touch targets only: the lab option resets min-height on desktop.
          '@media (pointer: coarse)': { minHeight: 44 },
          padding: '4px 8px',
          borderRadius: 4,
          fontSize: 14,
          '&[aria-selected="true"]': {
            backgroundColor: dark ? '#173e48' : brand.mist,
          },
          '&[data-focus="true"]': {
            backgroundColor: dark ? '#244854' : '#edf6f6',
          },
        },
      },
    },
    FixedWidthFormControlLabel: {
      styleOverrides: {
        root: { margin: 0, width: '100%', alignItems: 'center' },
        label: {
          minWidth: 0,
          flex: 1,
          '& svg': { fontSize: 18, flexShrink: 0, opacity: 0.65 },
        },
      },
    },
    CatalogReactUserListPicker: {
      styleOverrides: {
        root: { backgroundColor: 'transparent', border: 0, boxShadow: 'none' },
        title: {
          margin: '16px 8px 8px',
          fontWeight: 600,
          color: dark ? universusGray : '#515d70',
        },
        groupWrapper: {
          margin: '0 8px 12px',
          boxShadow: 'none',
          border: `1px solid ${border}`,
          borderRadius: 6,
        },
        menuItem: {
          minHeight: 44,
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: { defaultTab: { textTransform: 'none' as const } },
    },
    // Links must stand apart from body text (WCAG 1.4.1). In light mode
    // teal-600 manages it by colour alone: 4.65:1 on white and 3:1 against
    // body text. No dark-mode colour clears both 4.5:1 on the dark surfaces
    // and 3:1 against the light body text, so dark mode underlines instead.
    MuiLink: {
      styleOverrides: {
        root: {
          color: dark ? universusPrimaryLight : universusPrimaryText,
          '&:hover': { color: dark ? brand.mist : universusPrimaryDark },
        },
        ...(dark && {
          underlineHover: {
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 8 },
        elevation1: {
          border: `1px solid ${border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none' as const,
          minHeight: 36,
          '@media (pointer: coarse)': { minHeight: 44 },
          '&:focus-visible': {
            outline: `2px solid ${
              dark ? universusPrimary : universusPrimaryText
            }`,
            outlineOffset: 2,
          },
        },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
      },
    },
    MuiTab: { styleOverrides: { root: { textTransform: 'none' as const } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 6 },
        // Input outlines need 3:1 against the surface (WCAG 1.4.11); the
        // dark-mode divider colour is only about 1.6:1 on paper.
        notchedOutline: {
          borderColor: dark ? 'rgba(255,255,255,0.42)' : 'hsl(220, 14%, 60%)',
        },
      },
    },
  };
};

/**
 * Hide disabled icon-links (e.g. "View Source" / "View TechDocs") in the
 * AboutCard subheader.  Upstream Backstage renders them as greyed-out
 * placeholders even when the entity has no associated source-location or
 * techdocs-ref annotation; we prefer to hide them entirely.
 */
const iconLinkOverrides = {
  BackstageIconLinkVertical: {
    styleOverrides: {
      disabled: {
        display: 'none',
      },
    },
  },
};

/**
 * Sidebar component style overrides.
 *
 * These use MUI's theme component override mechanism (keyed by the component
 * `name` option, e.g. "BackstageSidebar") so that styles are applied via
 * the theme — not via CSS class-name selectors. This is critical because
 * MUI v4 minifies class names in production (e.g. "jss1" instead of
 * "BackstageSidebar-drawer-15"), which breaks attribute substring selectors.
 */
const sidebarComponentOverrides = {
  BackstageSidebar: {
    styleOverrides: {
      drawer: {
        // Sidebar dividers — target the <hr> elements directly because
        // BackstageSidebarDivider uses styled("hr") with a function style,
        // and MUI v4's deepmerge cannot merge a function with an object
        // override (it produces an empty object, losing all styles).
        '& hr': {
          opacity: 0.5,
        },
      },
    },
  },
  BackstageSidebarItem: {
    styleOverrides: {
      selected: {
        borderLeftWidth: '3px !important',
        backgroundColor: 'rgba(52, 167, 173, 0.18)',
      },
      buttonItem: {
        borderRadius: '0 !important',
      },
    },
  },
};

/**
 * Univerus Hub Light Theme
 * Based on Backstage's light theme with Univerus brand colors
 */
export const universusLightTheme = createUnifiedTheme({
  fontFamily,
  ...createBaseThemeOptions({
    fontFamily,
    typography,
    palette: {
      ...palettes.light,
      background: { default: lightPageBackground, paper: '#ffffff' },
      divider: 'hsl(220, 14%, 88%)',
      primary: {
        main: universusPrimaryText,
        light: brand.mist,
        dark: universusPrimaryDark,
        contrastText: universusLight,
      },
      secondary: {
        main: universusSecondaryDark,
        light: '#1a4d7a',
        dark: brand.navyDeep,
        contrastText: universusLight,
      },
      text: {
        primary: 'hsl(220, 20%, 18%)',
        secondary: 'hsl(220, 14%, 38%)',
      },
      // Backstage's own link colour (Markdown content), matching MuiLink below.
      link: universusPrimaryText,
      linkHover: universusPrimaryDark,
      success: { main: '#2e7d32', light: '#e3f2e4' },
      warning: { main: '#ed6c02', light: '#ffebd7' },
      error: { main: '#d32f2f', light: '#fadcd9' },
      info: { main: '#0288d1', light: '#ddeffa' },
      navigation: {
        background: universusSidebarBg, // Navigation navy
        indicator: universusPrimary, // Indicator color
        color: universusGray, // Sidebar text: #e6e7e8
        selectedColor: universusLight, // Selected item text: white for contrast
        navItem: {
          hoverBackground: 'rgba(255,255,255,0.06)',
        },
      },
    },
  }),
  components: {
    ...sidebarComponentOverrides,
    ...iconLinkOverrides,
    ...surfaceComponents(false),
  },
  defaultPageTheme: 'home',
  pageTheme: flatPageThemes(lightPageBackground, universusSecondaryDark),
});

/**
 * Univerus Hub Dark Theme
 * Based on Backstage's dark theme with Univerus brand colors
 */
export const universusDarkTheme = createUnifiedTheme({
  fontFamily,
  ...createBaseThemeOptions({
    fontFamily,
    typography,
    palette: {
      ...palettes.dark,
      primary: {
        main: universusPrimary,
        light: brand.mist,
        dark: universusPrimaryDark,
        contrastText: universusSecondaryDark,
      },
      secondary: {
        main: universusSky,
        light: brand.mist,
        dark: universusPrimary,
        contrastText: universusSecondaryDark,
      },
      // Secondary text is dimmer than primary, so selected tabs and headings
      // read as the emphasised ones. textSubtle is Backstage's own dark grey.
      text: {
        primary: universusGray,
        secondary: palettes.dark.textSubtle,
      },
      link: universusPrimaryLight,
      linkHover: brand.mist,
      navigation: {
        background: universusSidebarBg, // Navigation navy (same as light)
        indicator: universusPrimary,
        color: universusLight,
        selectedColor: universusLight, // White text for active item (matches light theme)
        navItem: {
          hoverBackground: 'rgba(255,255,255,0.06)',
        },
      },
    },
  }),
  components: {
    ...sidebarComponentOverrides,
    ...iconLinkOverrides,
    ...surfaceComponents(true),
  },
  defaultPageTheme: 'home',
  pageTheme: flatPageThemes(palettes.dark.background.default, universusGray),
});
